/**
 * Portfolio KPI Cards Component
 * Displays 6 key performance indicator cards for the aggregated portfolio dashboard
 */

import { Card, CardContent } from '@/components/ui/card'
import { TrendingUp, TrendingDown } from 'lucide-react'
import type { DashboardMetrics } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { cn } from '@/lib/utils'

export interface EngagementStatusCounts {
  active: number
  pending: number
  complete: number
}

interface PortfolioKPICardsProps {
  metrics: DashboardMetrics
  statusCounts?: EngagementStatusCounts
}

export function PortfolioKPICards({ metrics, statusCounts }: PortfolioKPICardsProps) {
  const { formatCurrency, formatPercent } = useFormatters()

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {/* Total Contract Value */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Total Contract Value</p>
          <p className="text-2xl font-bold leading-none mb-1.5">{formatCurrency(metrics.contractAmount)}</p>
        </CardContent>
      </Card>

      {/* Revenue to Date */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Revenue to Date</p>
          <p className="text-2xl font-bold leading-none mb-1.5">{formatCurrency(metrics.revenueToDate)}</p>
          {metrics.revenueTrend !== null && (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                metrics.revenueTrend >= 0
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              )}
            >
              {metrics.revenueTrend >= 0 ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              {metrics.revenueTrend >= 0 ? '+' : ''}
              {formatPercent(metrics.revenueTrend, 1)} from last month
            </span>
          )}
        </CardContent>
      </Card>

      {/* Actual Cost to Date */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Actual Cost to Date</p>
          <p className="text-2xl font-bold leading-none mb-1.5">{formatCurrency(metrics.costsToDate)}</p>
          {metrics.projectedCost > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
              {formatPercent((metrics.costsToDate / metrics.projectedCost) * 100, 1)} of projected
            </span>
          )}
        </CardContent>
      </Card>

      {/* Gross Margin (Avg) */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Gross Margin (Avg)</p>
          <p className="text-2xl font-bold leading-none mb-1.5">{formatPercent(metrics.grossMarginPercent, 0)}</p>
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              metrics.grossMarginPercent < 0
                ? 'bg-red-50 text-red-700'
                : metrics.grossMarginPercent < 20
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-green-50 text-green-700'
            )}
          >
            {metrics.grossMarginPercent < 0 ? 'Negative' : metrics.grossMarginPercent < 20 ? 'Warning' : 'Positive'}
          </span>
        </CardContent>
      </Card>

      {/* Unbilled Revenue */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Unbilled Revenue</p>
          <p
            className={cn(
              'text-2xl font-bold leading-none mb-1.5',
              metrics.unbilledRevenue < 0 ? 'text-red-600' : ''
            )}
          >
            {metrics.unbilledRevenue < 0
              ? `(${formatCurrency(Math.abs(metrics.unbilledRevenue))})`
              : formatCurrency(metrics.unbilledRevenue)}
          </p>
          {metrics.unbilledRevenue < 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
              <TrendingDown className="w-3 h-3" />
              Overbilled
            </span>
          )}
        </CardContent>
      </Card>

      {/* Active Engagements */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Active Engagements</p>
          <p className="text-2xl font-bold leading-none mb-2">
            {statusCounts?.active ?? '—'}
          </p>
          {statusCounts && (
            <div className="flex flex-wrap gap-1">
              {statusCounts.pending > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  {statusCounts.pending} Pending
                </span>
              )}
              {statusCounts.complete > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  {statusCounts.complete} Completed
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
